import { useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Plus, Trash2 } from 'lucide-react'
import { useProviderProfile, useUpdateProviderProfile } from '@/hooks/useProviderProfile'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import LoadingPage from '@/components/shared/LoadingPage'
import ErrorState from '@/components/shared/ErrorState'
import type { CredentialType } from '@/types/settings'
import {
  CREDENTIAL_OPTIONS,
  onboardingCredentialsSchema,
  stateLicenseSchema,
} from '@/schemas/onboarding-credentials'

// Profile page allows zero state licenses (distinct from onboarding, which
// requires at least one). Override state_licenses to drop the min(1) guard.
const providerProfileSchema = onboardingCredentialsSchema.extend({
  tax_id: z.string().min(1, 'Tax ID is required'),
  dea_number: z.string().optional(),
  dosespot_user_id: z.string().optional(),
  state_licenses: z.array(stateLicenseSchema),
})

type ProviderProfileForm = z.infer<typeof providerProfileSchema>

export default function ProviderProfilePage() {
  const { data: profile, isLoading, error, refetch } = useProviderProfile()
  const updateProfile = useUpdateProviderProfile()

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProviderProfileForm>({
    resolver: zodResolver(providerProfileSchema),
    defaultValues: {
      credentials: 'MD',
      npi: '',
      tax_id: '',
      dea_number: '',
      dosespot_user_id: '',
      state_licenses: [],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'state_licenses',
  })

  const credentials = watch('credentials')

  useEffect(() => {
    if (profile) {
      reset({
        credentials: profile.credentials,
        npi: profile.npi,
        tax_id: profile.tax_id,
        dea_number: profile.dea_number ?? '',
        dosespot_user_id: profile.dosespot_user_id ?? '',
        state_licenses: profile.state_licenses,
      })
    }
  }, [profile, reset])

  const onSubmit = async (data: ProviderProfileForm) => {
    if (!profile) return
    try {
      await updateProfile.mutateAsync({
        ...profile,
        credentials: data.credentials,
        npi: data.npi,
        tax_id: data.tax_id,
        dea_number: data.dea_number || undefined,
        dosespot_user_id: data.dosespot_user_id || undefined,
        state_licenses: data.state_licenses,
      })
      toast.success('Provider profile saved')
    } catch {
      toast.error('Failed to save provider profile')
    }
  }

  if (isLoading) {
    return <LoadingPage variant="form" />
  }

  if (error) {
    return <ErrorState message="Failed to load provider profile." onRetry={() => refetch()} />
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-slate-900">Provider Profile</h1>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Credentials</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="credentials">Credentials</Label>
                <Select
                  value={credentials}
                  onValueChange={(val) => setValue('credentials', val as CredentialType, { shouldValidate: true })}
                >
                  <SelectTrigger id="credentials">
                    <SelectValue placeholder="Select credentials" />
                  </SelectTrigger>
                  <SelectContent>
                    {CREDENTIAL_OPTIONS.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="npi">NPI</Label>
                <Input id="npi" placeholder="1234567890" {...register('npi')} />
                {errors.npi && <p className="text-sm text-red-600">{errors.npi.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="tax_id">Tax ID</Label>
                <Input id="tax_id" placeholder="12-3456789" {...register('tax_id')} />
                {errors.tax_id && <p className="text-sm text-red-600">{errors.tax_id.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="dea_number">DEA Number (optional)</Label>
                <Input id="dea_number" placeholder="FC1234567" {...register('dea_number')} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dosespot_user_id">DoseSpot User ID (optional)</Label>
                <Input id="dosespot_user_id" placeholder="Provided by DoseSpot" {...register('dosespot_user_id')} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>State Licenses</CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => append({ state: '', license_number: '' })}
              >
                <Plus className="h-4 w-4 mr-1" /> Add License
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {fields.length === 0 ? (
              <p className="text-sm text-slate-500">No state licenses added</p>
            ) : (
              <div className="space-y-3">
                {fields.map((field, index) => (
                  <div key={field.id} className="flex items-start gap-3">
                    <div className="flex-1 space-y-2">
                      <Label htmlFor={`state-${index}`}>State</Label>
                      <Input
                        id={`state-${index}`}
                        placeholder="TX"
                        {...register(`state_licenses.${index}.state`)}
                      />
                      {errors.state_licenses?.[index]?.state && (
                        <p className="text-sm text-red-600">{errors.state_licenses[index].state?.message}</p>
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <Label htmlFor={`license-${index}`}>License Number</Label>
                      <Input
                        id={`license-${index}`}
                        placeholder="TX-MD-123456"
                        {...register(`state_licenses.${index}.license_number`)}
                      />
                      {errors.state_licenses?.[index]?.license_number && (
                        <p className="text-sm text-red-600">{errors.state_licenses[index].license_number?.message}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="mt-7"
                      aria-label="Remove license"
                      onClick={() => remove(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Profile'}
        </Button>
      </form>
    </div>
  )
}
